// TEMPORARY MOCK ADAPTER for Notice Management (PRD 13).
// In-memory only — state lives in this module's `noticesStore` array and
// resets on hard reload. Mutations resolve with ~300 ms simulated latency.
// Errors mimic the backend envelope (NoticeApiError) per
// docs/api-contracts/notice-management.md.
// Backend switch: VITE_USE_MOCK_API=false flips to the real HTTP adapter.
// See notices.adapter.ts.

import {
  NoticeApiError,
  type CreateNoticeInput,
  type Notice,
  type NoticeApi,
  type NoticeAuthor,
  type NoticeSummary,
  type UpdateNoticeInput,
} from '../types/notices.types';
import { isContentTooLong } from '../utils/validation';

const LATENCY_MS = 300;

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));
const delay = (ms: number = LATENCY_MS): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// PRD 13 §3.2 — the author is the authenticated admin from the session. In the
// real BE this is server-derived; in mock mode we seed a default and let the UI
// override it with the logged-in admin (auth store) via setNoticeMockAuthor,
// wired from the create form in Slice 4.
const DEFAULT_AUTHOR: NoticeAuthor = {
  name: '강현우',
  email: 'hw.kang@carelink.co.kr',
  authorGrade: 'ROOT',
};

let currentAuthor: NoticeAuthor = { ...DEFAULT_AUTHOR };

export function setNoticeMockAuthor(author: NoticeAuthor): void {
  currentAuthor = { ...author };
}

const AUTHOR_OPERATION: NoticeAuthor = {
  name: '김운영',
  email: 'op.kim@carelink.co.kr',
  authorGrade: 'OPERATION',
};

const AUTHOR_CONTENT: NoticeAuthor = {
  name: '이콘텐츠',
  email: 'content.lee@carelink.co.kr',
  authorGrade: 'CONTENT',
};

// ── Seed: notices (PRD 13 §4.1) ──
// ~25 rows spanning every audience / category / status, some pushOnPublish,
// varied views + dates — exercises pagination (>20), sort, empty-search, and
// the push / status / audience badges.
function seed(): Notice[] {
  const rows: Array<Omit<Notice, 'createdAt'> & { createdAt: string }> = [
    { id: 1, audience: 'PATIENT', title: '[점검] 정기 시스템 점검 안내 (6/20)', category: 'SYSTEM', status: 'PUBLISHED', author: { ...DEFAULT_AUTHOR }, views: 1284, createdAt: '2026-06-12T09:30:00.000Z', pushOnPublish: true, content: '<p>안녕하세요. 보다 안정적인 서비스 제공을 위해 정기 점검을 실시합니다.</p><ul><li>일시: 6월 20일 02:00 ~ 04:00</li><li>영향: 일시적 접속 제한</li></ul>' },
    { id: 2, audience: 'BOTH', title: '개인정보 처리방침 개정 안내', category: 'GENERAL', status: 'PUBLISHED', author: { ...AUTHOR_OPERATION }, views: 932, createdAt: '2026-06-11T05:10:00.000Z', pushOnPublish: false, content: '<p>개인정보 처리방침이 2026년 7월 1일부로 개정됩니다.</p>' },
    { id: 3, audience: 'STAFF', title: '의료진 대시보드 v2.3 업데이트', category: 'UPDATE', status: 'PUBLISHED', author: { ...AUTHOR_CONTENT }, views: 421, createdAt: '2026-06-10T23:45:00.000Z', pushOnPublish: false, content: '<p>차트 조회 속도가 개선되었습니다.</p>' },
    { id: 4, audience: 'PATIENT', title: '신규 건강 콘텐츠가 추가되었어요', category: 'CONTENT', status: 'PUBLISHED', author: { ...AUTHOR_CONTENT }, views: 2207, createdAt: '2026-06-09T01:20:00.000Z', pushOnPublish: true, content: '<p>수면 관리 가이드 콘텐츠가 새로 추가되었습니다.</p>' },
    { id: 5, audience: 'PATIENT', title: '여름철 건강관리 캠페인 안내', category: 'GENERAL', status: 'DRAFT', author: { ...DEFAULT_AUTHOR }, views: 0, createdAt: '2026-06-08T07:55:00.000Z', pushOnPublish: false, content: '<p>(초안) 폭염 대비 건강관리 수칙 안내 예정.</p>' },
    { id: 6, audience: 'BOTH', title: '추석 연휴 고객센터 운영 안내', category: 'GENERAL', status: 'PUBLISHED', author: { ...AUTHOR_OPERATION }, views: 588, createdAt: '2026-06-07T02:00:00.000Z', pushOnPublish: false, content: '<p>연휴 기간 고객센터 운영 시간이 조정됩니다.</p>' },
    { id: 7, audience: 'STAFF', title: '처방 모듈 긴급 패치 적용', category: 'SYSTEM', status: 'PUBLISHED', author: { ...DEFAULT_AUTHOR }, views: 173, createdAt: '2026-06-06T11:32:00.000Z', pushOnPublish: false, content: '<p>처방 모듈 보안 패치가 적용되었습니다.</p>' },
    { id: 8, audience: 'PATIENT', title: '앱 푸시 알림 설정 방법 안내', category: 'CONTENT', status: 'PUBLISHED', author: { ...AUTHOR_CONTENT }, views: 1640, createdAt: '2026-06-05T08:15:00.000Z', pushOnPublish: true, content: '<p>알림 설정 방법을 안내합니다.</p>' },
    { id: 9, audience: 'BOTH', title: '서비스 이용약관 변경 사전 고지', category: 'GENERAL', status: 'HIDDEN', author: { ...AUTHOR_OPERATION }, views: 77, createdAt: '2026-06-04T04:40:00.000Z', pushOnPublish: false, content: '<p>(숨김 처리됨) 약관 변경 고지 초안.</p>' },
    { id: 10, audience: 'PATIENT', title: '건강검진 결과 조회 기능 오픈', category: 'UPDATE', status: 'PUBLISHED', author: { ...AUTHOR_CONTENT }, views: 3012, createdAt: '2026-06-03T00:05:00.000Z', pushOnPublish: true, content: '<p>이제 앱에서 검진 결과를 바로 확인할 수 있습니다.</p>' },
    { id: 11, audience: 'STAFF', title: '의료진 웹 접속 인증 정책 변경', category: 'SYSTEM', status: 'PUBLISHED', author: { ...DEFAULT_AUTHOR }, views: 244, createdAt: '2026-06-02T06:25:00.000Z', pushOnPublish: false, content: '<p>2단계 인증이 의무화됩니다.</p>' },
    { id: 12, audience: 'PATIENT', title: '복약 알림 기능 개선 안내', category: 'UPDATE', status: 'PUBLISHED', author: { ...AUTHOR_CONTENT }, views: 1925, createdAt: '2026-06-01T09:10:00.000Z', pushOnPublish: false, content: '<p>복약 알림 정확도가 개선되었습니다.</p>' },
    { id: 13, audience: 'BOTH', title: '시스템 안정화 작업 완료 보고', category: 'SYSTEM', status: 'PUBLISHED', author: { ...AUTHOR_OPERATION }, views: 410, createdAt: '2026-05-30T22:00:00.000Z', pushOnPublish: false, content: '<p>지난 점검 이후 안정화 작업이 완료되었습니다.</p>' },
    { id: 14, audience: 'PATIENT', title: '마음 건강 자가진단 콘텐츠 추가', category: 'CONTENT', status: 'DRAFT', author: { ...AUTHOR_CONTENT }, views: 0, createdAt: '2026-05-29T03:48:00.000Z', pushOnPublish: false, content: '<p>(초안) 자가진단 도구 추가 예정.</p>' },
    { id: 15, audience: 'STAFF', title: '환자 문의 처리 가이드 업데이트', category: 'CONTENT', status: 'PUBLISHED', author: { ...AUTHOR_OPERATION }, views: 198, createdAt: '2026-05-28T07:30:00.000Z', pushOnPublish: false, content: '<p>문의 응대 가이드가 갱신되었습니다.</p>' },
    { id: 16, audience: 'PATIENT', title: '5월 서비스 점검 결과 안내', category: 'SYSTEM', status: 'PUBLISHED', author: { ...DEFAULT_AUTHOR }, views: 866, createdAt: '2026-05-27T01:15:00.000Z', pushOnPublish: false, content: '<p>5월 점검이 정상 완료되었습니다.</p>' },
    { id: 17, audience: 'BOTH', title: '비밀번호 보안 강화 권고', category: 'GENERAL', status: 'PUBLISHED', author: { ...AUTHOR_OPERATION }, views: 1102, createdAt: '2026-05-26T05:00:00.000Z', pushOnPublish: false, content: '<p>주기적인 비밀번호 변경을 권장합니다.</p>' },
    { id: 18, audience: 'PATIENT', title: '걷기 챌린지 이벤트 오픈', category: 'CONTENT', status: 'PUBLISHED', author: { ...AUTHOR_CONTENT }, views: 4521, createdAt: '2026-05-25T08:40:00.000Z', pushOnPublish: true, content: '<p>6월 걷기 챌린지가 시작됩니다.</p>' },
    { id: 19, audience: 'STAFF', title: '진료 통계 리포트 메뉴 추가', category: 'UPDATE', status: 'PUBLISHED', author: { ...DEFAULT_AUTHOR }, views: 312, createdAt: '2026-05-24T02:22:00.000Z', pushOnPublish: false, content: '<p>통계 리포트 메뉴가 추가되었습니다.</p>' },
    { id: 20, audience: 'PATIENT', title: '앱 버전 4.0 업데이트 안내', category: 'UPDATE', status: 'PUBLISHED', author: { ...AUTHOR_CONTENT }, views: 5230, createdAt: '2026-05-23T00:30:00.000Z', pushOnPublish: true, content: '<p>새로운 홈 화면이 적용되었습니다.</p>' },
    { id: 21, audience: 'BOTH', title: '개인정보 수집 항목 변경 안내', category: 'GENERAL', status: 'PUBLISHED', author: { ...AUTHOR_OPERATION }, views: 720, createdAt: '2026-05-22T06:10:00.000Z', pushOnPublish: false, content: '<p>수집 항목이 일부 변경됩니다.</p>' },
    { id: 22, audience: 'PATIENT', title: '심리 상담 예약 기능 베타 오픈', category: 'CONTENT', status: 'DRAFT', author: { ...AUTHOR_CONTENT }, views: 0, createdAt: '2026-05-21T09:00:00.000Z', pushOnPublish: false, content: '<p>(초안) 상담 예약 베타 안내.</p>' },
    { id: 23, audience: 'STAFF', title: '의료진 계정 비활성 정책 안내', category: 'SYSTEM', status: 'PUBLISHED', author: { ...DEFAULT_AUTHOR }, views: 156, createdAt: '2026-05-20T03:33:00.000Z', pushOnPublish: false, content: '<p>90일 미접속 계정은 비활성 처리됩니다.</p>' },
    { id: 24, audience: 'PATIENT', title: '여름 휴가철 비대면 진료 안내', category: 'GENERAL', status: 'PUBLISHED', author: { ...AUTHOR_OPERATION }, views: 1377, createdAt: '2026-05-19T07:48:00.000Z', pushOnPublish: false, content: '<p>휴가철 비대면 진료 이용 방법을 안내합니다.</p>' },
    { id: 25, audience: 'BOTH', title: '서비스 만족도 설문 참여 요청', category: 'CONTENT', status: 'PUBLISHED', author: { ...AUTHOR_CONTENT }, views: 489, createdAt: '2026-05-18T01:05:00.000Z', pushOnPublish: false, content: '<p>설문에 참여해 주세요.</p>' },
  ];
  return rows;
}

let noticesStore: Notice[] = seed();
let nextId = 26;

// ── Helpers ──
const TITLE_MIN = 2;
const TITLE_MAX = 100;

function findNotice(id: number): Notice {
  const n = noticesStore.find((x) => x.id === id);
  if (!n) throw new NoticeApiError('NOT_FOUND', '존재하지 않거나 삭제된 게시물입니다.');
  return n;
}

function toSummary(n: Notice): NoticeSummary {
  // Strip the heavy `content` field for list responses (contract §2.2).
  const { content, ...rest } = n;
  void content;
  return rest;
}

function nowIso(): string {
  return new Date().toISOString();
}

// PRD 13 §4.2 — non-empty after stripping tags.
function hasContent(html: string | undefined): boolean {
  if (!html) return false;
  const stripped = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  return stripped.length > 0;
}

function validateTitle(value: string | undefined): string {
  const trimmed = (value ?? '').trim();
  if (trimmed.length < TITLE_MIN || trimmed.length > TITLE_MAX) {
    throw new NoticeApiError('TITLE_LENGTH_INVALID', '제목을 입력해주세요. (2~100자)', {
      fields: { title: '제목을 입력해주세요. (2~100자)' },
    });
  }
  return trimmed;
}

function validateContent(value: string | undefined): string {
  if (!hasContent(value)) {
    throw new NoticeApiError('CONTENT_REQUIRED', '내용을 입력해주세요.', {
      fields: { content: '내용을 입력해주세요.' },
    });
  }
  // [공통] 00 §7-k — rich text max 20,000 chars (HTML included).
  if (isContentTooLong(value as string)) {
    throw new NoticeApiError('CONTENT_TOO_LONG', '내용은 최대 20,000자까지 입력할 수 있습니다.', {
      fields: { content: '내용은 최대 20,000자까지 입력할 수 있습니다.' },
    });
  }
  return value as string;
}

// Sort the list response by createdAt DESC (PRD 13 §3.1 default).
function sortByCreatedDesc(arr: Notice[]): Notice[] {
  return [...arr].sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0));
}

export const noticesMockApi: NoticeApi = {
  async list(): Promise<NoticeSummary[]> {
    await delay();
    return clone(sortByCreatedDesc(noticesStore).map(toSummary));
  },

  async get(id: number): Promise<Notice> {
    await delay();
    return clone(findNotice(id));
  },

  async create(input: CreateNoticeInput): Promise<Notice> {
    await delay();
    const title = validateTitle(input.title);
    const content = validateContent(input.content);
    // PRD 13 §4.3 — STAFF audience can never push to the patient app.
    const pushOnPublish = input.audience === 'STAFF' ? false : input.pushOnPublish;

    const created: Notice = {
      id: nextId++,
      audience: input.audience,
      title,
      category: input.category,
      status: input.status,
      author: { ...currentAuthor },
      views: 0,
      createdAt: nowIso(),
      pushOnPublish,
      content,
    };
    noticesStore = [created, ...noticesStore];
    return clone(created);
  },

  async update(id: number, input: UpdateNoticeInput): Promise<Notice> {
    await delay();
    const target = findNotice(id);

    if (input.title !== undefined) target.title = validateTitle(input.title);
    if (input.content !== undefined) target.content = validateContent(input.content);
    if (input.category !== undefined) target.category = input.category;
    if (input.status !== undefined) target.status = input.status;
    if (input.audience !== undefined) target.audience = input.audience;
    if (input.audience !== undefined || input.pushOnPublish !== undefined) {
      const audience = input.audience ?? target.audience;
      const push = input.pushOnPublish ?? target.pushOnPublish;
      // STAFF audience forces pushOnPublish false (PRD 13 §4.3). Author snapshot
      // is intentionally never touched on update (PRD 13 §3.3).
      target.pushOnPublish = audience === 'STAFF' ? false : push;
    }
    return clone(target);
  },

  async remove(id: number): Promise<void> {
    await delay();
    findNotice(id);
    noticesStore = noticesStore.filter((n) => n.id !== id);
  },
};
